import React, { useCallback, useRef, useState, useEffect } from "react";
import TextInput from "uicomponents/TextInput";
import SelectInput from "uicomponents/SelectInput";
import RadioInput from "uicomponents/RadioInput";
import { sectionsQuestions } from "pages/WorkersCompApplcation/utils/sectionsQuestions";
import { useFormikContext } from "formik";
import { BusinessDetailsFormType } from "pages/WorkersCompApplcation/utils/formType";
import { BusinessDetailsType } from "types";
import { useOktaAuth } from "@okta/okta-react";
import { getWWRedirectUrl, hasQueryParam } from "utils/helper";

import RelatedEntitiesDetails from "./RelatedEntitiesDetails";
import styles from "../BusinessDetails.module.css";
import { entityTypeOptions } from "../../../utils/selectInputOptions";

interface IBusinessDetailsComponentProps {
  data: BusinessDetailsType;
  fieldRefs: React.MutableRefObject<{ [key: string]: HTMLInputElement | null }>;
}
interface NaicsCodeOption {
  name: string;
  value: string;
}

const BusinessDetailsComponent = ({
  ...props
}: IBusinessDetailsComponentProps): JSX.Element => {
  const { data, fieldRefs } = props;
  const { values, setFieldValue } = useFormikContext<BusinessDetailsFormType>();
  const { authState } = useOktaAuth();

  const wc_flow_with_core_app = hasQueryParam("usecoreapp");
  const labelList = sectionsQuestions["businessDetails"].questions;
  const messageList = sectionsQuestions["businessDetails"].message;

  const hasOwnerHalfStakeOther = values?.ownerHalfStakeOther;
  const hasIndustry = values?.industry;
  const hasFourDigitNaics = values?.industryNaics4;

  const prevIndustryRef = useRef(hasIndustry);
  const prevFourDigitNaicsRef = useRef(hasFourDigitNaics);

  const [twoDigitNaicsOptions, setTwoDigitNaicsOptions] = useState<
    NaicsCodeOption[]
  >([]);
  const [fourDigitNaicsOptions, setFourDigitNaicsOptions] = useState<
    NaicsCodeOption[]
  >([]);
  const [sixDigitNaicsOptions, setSixDigitNaicsOptions] = useState<
    NaicsCodeOption[]
  >([]);

  function getNACISPrefix(naicsValue: string, length: number): string {
    const isNAICSRange = naicsValue[length] === "-";
    return naicsValue.slice(0, isNAICSRange ? length * 2 + 1 : length);
  }

  const fetchNaicsOptions = useCallback(
    async (
      setOptions: (data: NaicsCodeOption[]) => void,
      prefix: string,
      length: number
    ) => {
      const urlString = `/naics?prefix=${prefix}&length=${length}`;
      const fetchUrl = getWWRedirectUrl(urlString);

      try {
        const response = await fetch(fetchUrl, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: "Bearer " + authState?.accessToken?.accessToken,
          },
        });
        if (!response.ok) {
          return [];
        }
        const result = await response.json();

        // add "Other" option on the 2 digit naics results
        if (length === 2) {
          result.push({ name: "Other", value: "Other" });
        }
        setOptions(result);
      } catch (error) {
        console.error(`Error fetching NAICS options:`, error);
      }
    },
    [authState]
  );

  useEffect(() => {
    // request 2 digit naics codes
    fetchNaicsOptions(setTwoDigitNaicsOptions, "", 2);
  }, [fetchNaicsOptions]);

  useEffect(() => {
    if (hasIndustry !== "Other") {
      setFieldValue("industryOther", "");
    }
  }, [hasIndustry, setFieldValue]);

  useEffect(() => {
    if (hasIndustry) {
      // request 4 digit naics codes for 2-digit selection (excluding 'Other')
      const twoDigitPrefix = getNACISPrefix(hasIndustry, 2);
      if (twoDigitPrefix !== "Ot") {
        fetchNaicsOptions(setFourDigitNaicsOptions, twoDigitPrefix, 4);
      }
    }
    if (hasIndustry !== prevIndustryRef.current) {
      setFieldValue("industryNaics4", "");
      setFieldValue("industryNaics6", "");
    }
    prevIndustryRef.current = hasIndustry;
  }, [hasIndustry, fetchNaicsOptions, setFieldValue]);

  useEffect(() => {
    if (hasFourDigitNaics) {
      // request 6 digit naics codes for 4-digit selection
      const fourDigitPrefix = getNACISPrefix(hasFourDigitNaics, 4);
      fetchNaicsOptions(setSixDigitNaicsOptions, fourDigitPrefix, 6);
    }
    if (hasFourDigitNaics !== prevFourDigitNaicsRef.current) {
      setFieldValue("industryNaics6", "");
    }

    prevFourDigitNaicsRef.current = hasFourDigitNaics;
  }, [hasFourDigitNaics, fetchNaicsOptions, setFieldValue]);

  useEffect(() => {
    if (hasOwnerHalfStakeOther === "No") {
      setFieldValue("relatedEntities", [
        {
          reName: "",
          reDescription: "",
          reIsDependent: "",
          reDba: "",
          reEin: "",
          reHasW2Employees: "",
          reHasOverlappingEmployees: "",
          reSharesLocation: "",
        },
      ]);
    }
  }, [hasOwnerHalfStakeOther, setFieldValue]);

  return (
    <div>
      <div className={styles.title}>
        {sectionsQuestions["businessDetails"].title}
      </div>
      <div className={styles.description}>
        {sectionsQuestions["businessDetails"].description}
      </div>
      {!wc_flow_with_core_app && (
        <>
          <TextInput
            name="companyName"
            type="string"
            label={labelList.companyName}
            message={messageList.companyName}
            fieldRefs={fieldRefs}
          />
          <TextInput
            name="dba"
            type="string"
            label={labelList.dba}
            required={false}
          />
        </>
      )}
      <TextInput
        name="ein"
        type="string"
        label={labelList.ein}
        fieldRefs={fieldRefs}
      />
      {!wc_flow_with_core_app && (
        <>
          <SelectInput
            name="entityType"
            options={entityTypeOptions}
            label={labelList.entityType}
            placeholder="Select an option"
            fieldRefs={fieldRefs}
          />
          <SelectInput
            name="industry"
            options={twoDigitNaicsOptions}
            label={labelList.industry}
            placeholder="Select an option"
            fieldRefs={fieldRefs}
          />
          {hasIndustry === "Other" && (
            <TextInput
              name="industryOther"
              type="string"
              label={labelList.industryOther}
              fieldRefs={fieldRefs}
            />
          )}
          {hasIndustry !== "Other" && (
            <>
              {!!hasIndustry && (
                <SelectInput
                  name="industryNaics4"
                  options={fourDigitNaicsOptions}
                  label={labelList.industryNaics4}
                  placeholder="Select an option"
                  fieldRefs={fieldRefs}
                />
              )}
              {!!hasFourDigitNaics && (
                <SelectInput
                  name="industryNaics6"
                  options={sixDigitNaicsOptions}
                  label={labelList.industryNaics6}
                  placeholder="Select an option"
                  fieldRefs={fieldRefs}
                />
              )}
            </>
          )}
        </>
      )}
      <RadioInput
        name="isNonProfit"
        label={labelList.isNonProfit}
        options={["Yes", "No"]}
        fieldRefs={fieldRefs}
      />
      {!wc_flow_with_core_app && (
        <TextInput
          name="yearFounded"
          type="string"
          label={labelList.yearFounded}
          fieldRefs={fieldRefs}
          placeholder="YYYY"
        />
      )}
      <TextInput
        name="hoursOfOperation"
        type="string"
        label={labelList.hoursOfOperation}
        fieldRefs={fieldRefs}
        placeholder="9–5"
      />
      <RadioInput
        name="ownerHalfStakeOther"
        label={labelList.ownerHalfStakeOther(values.companyName)}
        options={["Yes", "No"]}
        message={messageList.ownerHalfStakeOther}
        fieldRefs={fieldRefs}
      />
      {hasOwnerHalfStakeOther === "Yes" && (
        <RelatedEntitiesDetails
          applicationData={
            data?.workersCompApplication && data.workersCompApplication[0]
          }
          fieldRefs={fieldRefs}
        />
      )}
    </div>
  );
};

export default BusinessDetailsComponent;
